// CSV import modal — two-step flow that opens from Contacts > Import CSV.
//
// Step 1: paste or upload CSV → hits /analyze → shows detected columns +
//         auto-map + a preview of the first 5 rows
// Step 2: user tweaks the mapping (or accepts as-is) → clicks Import →
//         hits /commit → shows { imported, duplicates, errors }
//
// Handles the top 4 export formats: GHL, HubSpot, Housecall Pro,
// ServiceTitan. Falls back to fuzzy header matching for anything else.

import React, { useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Select, Textarea, Modal, Spinner } from '../ui.jsx';

export function CsvImportModal({ onClose, onImported, toast }) {
  const [step, setStep] = useState('paste');   // paste → map → result
  const [csv, setCsv] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [source, setSource] = useState('CSV Import');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsv(String(r.result || ''));
    r.readAsText(f);
  };

  const analyze = async () => {
    if (!csv.trim()) return toast('Paste CSV or upload a file first', 'error');
    setBusy(true);
    try {
      const r = await api('/api/csv-import/analyze', { method:'POST', body:{ csv }});
      setAnalysis(r);
      setMapping(r.autoMap || {});
      setSource(r.guessedSource || 'CSV Import');
      setStep('map');
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api('/api/csv-import/commit', { method:'POST', body:{ csv, mapping, source, tags }});
      setResult(r);
      setStep('result');
      onImported?.(r);
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  return <Modal open onClose={onClose} width={780}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:14 }}>
      <h2 style={{ margin:0, fontSize:20 }}>📥 Import Contacts from CSV</h2>
      <div style={{ display:'flex', gap:4, fontSize:11, color:G.textMuted }}>
        {['paste','map','result'].map((s, i) => (
          <div key={s} style={{ padding:'4px 10px', borderRadius:12,
            background: step === s ? G.purple : (['paste','map','result'].indexOf(step) > i ? G.green+'22' : G.input),
            color: step === s ? '#fff' : (['paste','map','result'].indexOf(step) > i ? G.green : G.textMuted),
            fontWeight:700, textTransform:'uppercase', letterSpacing:'.04em',
          }}>{i+1}. {s}</div>
        ))}
      </div>
    </div>

    {step === 'paste' && <div>
      <div style={{ color:G.textMuted, fontSize:13, marginBottom:12, lineHeight:1.5 }}>
        Export contacts from GHL / HubSpot / Housecall Pro / ServiceTitan → save as CSV → drop the file here or paste the contents.
      </div>
      <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ marginBottom:10 }}/>
      <Field label="Or paste CSV contents">
        <Textarea value={csv} onChange={e=>setCsv(e.target.value)} placeholder="first_name,last_name,phone,email,..." style={{ minHeight:180, fontFamily:'ui-monospace, monospace', fontSize:12 }}/>
      </Field>
      <Btn v="primary" full onClick={analyze} disabled={busy || !csv.trim()}>{busy ? 'Analyzing…' : 'Analyze CSV →'}</Btn>
    </div>}

    {step === 'map' && analysis && <div>
      <div style={{ padding:12, background:G.input, borderRadius:8, marginBottom:14, fontSize:13 }}>
        <div><strong>{analysis.totalRows}</strong> rows detected · Guessed source: <strong>{analysis.guessedSource}</strong></div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>We auto-mapped what we could. Review below — leave a column set to "(ignore)" to skip it.</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
        <Field label="Source label"><Input value={source} onChange={e=>setSource(e.target.value)}/></Field>
        <Field label="Apply tag(s)"><Input value={tags} onChange={e=>setTags(e.target.value)} placeholder="imported, past-customer"/></Field>
      </div>

      <div style={{ display:'grid', gap:6, marginBottom:14, maxHeight:280, overflow:'auto', padding:8, background:G.input, borderRadius:8 }}>
        {analysis.headers.map(h => (
          <div key={h} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, alignItems:'center' }}>
            <div style={{ fontSize:13, fontFamily:'ui-monospace, monospace', color:G.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h}</div>
            <Select value={mapping[h] || ''} onChange={e => setMapping({ ...mapping, [h]: e.target.value })} style={{ fontSize:12 }}>
              <option value="">(ignore)</option>
              {analysis.supportedFields.map(f => <option key={f} value={f}>{f.replace(/_/g,' ')}</option>)}
            </Select>
          </div>
        ))}
      </div>

      <details style={{ marginBottom:14, fontSize:12, color:G.textMuted }}>
        <summary style={{ cursor:'pointer' }}>Preview first 5 rows</summary>
        <div style={{ marginTop:8, overflow:'auto' }}>
          <table style={{ borderCollapse:'collapse', fontSize:11, width:'100%' }}>
            <thead><tr>{analysis.headers.map(h => <th key={h} style={{ padding:'4px 6px', borderBottom:`1px solid ${G.border}`, textAlign:'left', color:G.textMuted }}>{h}</th>)}</tr></thead>
            <tbody>
              {analysis.sampleRows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} style={{ padding:'4px 6px', borderBottom:`1px solid ${G.border}`, whiteSpace:'nowrap' }}>{c}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      </details>

      <div style={{ display:'flex', gap:8 }}>
        <Btn v="ghost" onClick={()=>setStep('paste')}>← Back</Btn>
        <Btn v="primary" full onClick={commit} disabled={busy}>{busy ? 'Importing…' : `Import ${analysis.totalRows} contacts →`}</Btn>
      </div>
    </div>}

    {step === 'result' && result && <div>
      <div style={{ padding:20, textAlign:'center' }}>
        <div style={{ fontSize:56, marginBottom:10 }}>✓</div>
        <h2 style={{ margin:0, color:G.green, fontSize:22 }}>Import complete</h2>
        <div style={{ marginTop:14, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
          <Stat label="Imported"   value={result.imported}  color={G.green}/>
          <Stat label="Duplicates" value={result.duplicates} color={G.textMuted}/>
          <Stat label="Errors"     value={result.errors}    color={result.errors ? G.red : G.textMuted}/>
        </div>
        <div style={{ marginTop:16, fontSize:12, color:G.textMuted }}>Duplicates were merged (notes appended). Errors were rows missing both phone and email.</div>
      </div>
      <Btn v="primary" full onClick={onClose}>Done</Btn>
    </div>}
  </Modal>;
}

function Stat({ label, value, color }) {
  return <div style={{ background:G.input, padding:'12px 10px', borderRadius:8, textAlign:'center' }}>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:26, fontWeight:800, color, marginTop:2 }}>{value}</div>
  </div>;
}
